import React, { useEffect, useRef, useState, } from "react";
import "../App.css";
import styled from "styled-components";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./Navbar";
import Controls from "./Controls";
import StylesSection from "./StylesSection";
import SongsSection from "./SongsSection";
import FavoritesSection from "./FavoritesSection";
import StyleSettings from "./settings/styleSettings/StyleSettings.js";
import SongSettings from "./settings/songSettings/SongSettings.js";
import Popover from "./popover/Popover";
import PopoverContentContainer from "./popover/PopoverContentContainer";
import About from "./popover/content/About";
import DonateBitcoin from "./popover/content/DonateBitcoin";
import Contact from "./popover/content/Contact";
import {
    MELOGY_TRANSPARENT,
    MELOGY_ALMOST_OPAQUE,
} from "../style/colors";

const MainContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
`;

const NavbarContainer = styled.div`
    width: 100%;
    position: fixed;
    top: 0;
    z-index: 3;
    background-color: rgba(0, 0, 0, 0.8); /* Reduced transparency - more opaque */
    pointer-events: none; /* Allow clicks to pass through to content below */
    
    /* Re-enable pointer events for interactive elements */
    & > * {
        pointer-events: auto;
    }
`;

const ContentContainer = styled.div`
    width: 100%;
    height: 100vh; /* Full height - navbar and controls float over */
    padding-top: 32px; /* Padding to allow content to scroll underneath fixed navbar */
    padding-bottom: 64px; /* Padding to allow content to scroll underneath fixed controls */
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    box-sizing: border-box;
`;

const Section = styled.div`
    width: 100%;
    min-height: 300px; /* Minimum height for each section */
    flex: 1 1 0; /* Equal distribution, but can grow */
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
`;

// Controls Section - History and Download buttons
const ControlsSection = styled.div`
    width: 100%;
    position: fixed;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8); /* Reduced transparency - more opaque */
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 0;
    pointer-events: none; /* Allow clicks to pass through to content below */
    
    /* Re-enable pointer events for interactive elements */
    & > * {
        pointer-events: auto;
    }
`;


function Main({
    songs,
    setSelectedSong,
    selectedSong,
    selectedInstruments,
    setSongName,
    isNameUnique,
    createAndFetchMidi,
    toggleLeftSideBar,
    styles,
    selectedStyle,
    updateStyle,
    setSelectedStyle,
    favorites,
    toggleFavorite,
    isFavorite,
    updateSong,
}) {
    const initialSongCreatedRef = useRef(false);
    const [styleSettingsIsOpen, setStyleSettingsIsOpen] = useState(false);
    const [songSettingsIsOpen, setSongSettingsIsOpen] = useState(false);
    const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
    const [aboutPopoverOpen, setAboutPopoverOpen] = useState(false);
    const [donatePopoverOpen, setDonatePopoverOpen] = useState(false);
    const [contactPopoverOpen, setContactPopoverOpen] = useState(false);
    const midi = selectedSong ? selectedSong.midi : null;

    const toggleAutoPlay = () => {
        setIsAutoPlayEnabled(!isAutoPlayEnabled);
    };

    const toggleAboutPopover = () => {
        setAboutPopoverOpen(!aboutPopoverOpen);
    };

    const toggleDonatePopover = () => {
        setDonatePopoverOpen(!donatePopoverOpen);
    };

    const toggleContactPopover = () => {
        setContactPopoverOpen(!contactPopoverOpen);
    };

    const closeOverlay = () => {
        setStyleSettingsIsOpen(false);
        setSelectedStyle(null);
    };

    const closeSongOverlay = () => {
        setSongSettingsIsOpen(false);
    };

    const handleCreateWithStyle = (style) => {
        setSelectedStyle(style);
        createAndFetchMidi(style);
    };

    useEffect(() => {
        if (songs.length === 0 && !initialSongCreatedRef.current) {
            if (styles.length > 0) {
                const firstStyle = styles[0];
                setSelectedStyle(firstStyle);
                createAndFetchMidi(firstStyle);
                initialSongCreatedRef.current = true;
            }
        }
    }, [songs.length, styles.length]); 

    // Count how many favorite songs there are
    const favoriteSongsCount = songs.filter(song => isFavorite(song.name)).length;
    const hasFavorites = favoriteSongsCount > 0;
    
    // Calculate section count (2 if no favorites, 3 if favorites exist)
    const sectionCount = hasFavorites ? 3 : 2;

    return (
        <MainContainer>
            <NavbarContainer>
                <Navbar selectedSong={selectedSong} />
            </NavbarContainer>
            
            <ContentContainer>
                {/* Styles Section - Top */}
                <Section sectionCount={sectionCount}>
                    <StylesSection
                        styles={styles}
                        createAndFetchMidi={handleCreateWithStyle}
                        setSelectedStyle={setSelectedStyle}
                        setStyleSettingsIsOpen={setStyleSettingsIsOpen}
                        selectedStyle={selectedStyle}
                    />
                </Section>

                {/* Songs Section - Middle */}
                <Section sectionCount={sectionCount}>
                    <SongsSection
                        songs={songs}
                        selectedSong={selectedSong}
                        setSelectedSong={setSelectedSong}
                        selectedInstruments={selectedInstruments}
                        selectedStyle={selectedStyle}
                        isAutoPlayEnabled={isAutoPlayEnabled}
                        createAndFetchMidi={createAndFetchMidi}
                        setSelectedStyle={setSelectedStyle}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                        isFavorite={isFavorite}
                        setSongSettingsIsOpen={setSongSettingsIsOpen}
                        updateSong={updateSong}
                    />
                </Section>

                {/* Favorites Section - Bottom (only if there are favorites) */}
                {hasFavorites && (
                    <Section sectionCount={sectionCount}>
                        <FavoritesSection
                            songs={songs}
                            selectedSong={selectedSong}
                            setSelectedSong={setSelectedSong}
                            selectedInstruments={selectedInstruments}
                            selectedStyle={selectedStyle}
                            isAutoPlayEnabled={isAutoPlayEnabled}
                            createAndFetchMidi={createAndFetchMidi}
                            setSelectedStyle={setSelectedStyle}
                            favorites={favorites}
                            toggleFavorite={toggleFavorite}
                            isFavorite={isFavorite}
                            setSongSettingsIsOpen={setSongSettingsIsOpen}
                            updateSong={updateSong}
                        />
                    </Section>
                )}
            </ContentContainer>

            {/* Controls Section - History and Download */}
            <ControlsSection>
                <Controls
                    createAndFetchMidi={createAndFetchMidi}
                    toggleLeftSideBar={toggleLeftSideBar}
                    midi={midi}
                    showCreateButton={false}
                    toggleAutoPlay={toggleAutoPlay}
                    isAutoPlayEnabled={isAutoPlayEnabled}
                    toggleAboutPopover={toggleAboutPopover}
                    toggleDonatePopover={toggleDonatePopover}
                    toggleContactPopover={toggleContactPopover}
                />
            </ControlsSection>
            
            {/* Style Settings Overlay */}
            {styleSettingsIsOpen && selectedStyle && (
                <StyleSettings 
                    styles={styles}
                    selectedStyle={selectedStyle} 
                    closeOverlay={closeOverlay}
                    updateStyle={updateStyle}
                    setSelectedStyle={setSelectedStyle}
                    setStyleSettingsIsOpen={setStyleSettingsIsOpen}
                    createAndFetchMidi={createAndFetchMidi}
                />
            )}
            
            {/* Song Settings Overlay */}
            {songSettingsIsOpen && selectedSong && (
                <SongSettings 
                    selectedSong={selectedSong} 
                    closeOverlay={closeSongOverlay}
                    setSelectedSong={setSelectedSong}
                    setSongSettingsIsOpen={setSongSettingsIsOpen}
                    updateSong={updateSong}
                    selectedInstruments={selectedInstruments}
                    selectedStyle={selectedStyle}
                    setSongName={setSongName}
                    isNameUnique={isNameUnique}
                />
            )}
            
            {/* About Popover */}
            {aboutPopoverOpen && (
                <Popover
                    content={
                        <PopoverContentContainer
                            title="ABOUT"
                            component={<About />}
                        />
                    }
                    onClose={toggleAboutPopover}
                    zIndex={1100}
                />
            )}

            {/* Donate Popover */}
            {donatePopoverOpen && (
                <Popover
                    content={
                        <PopoverContentContainer
                            title="DONATE BITCOIN"
                            component={<DonateBitcoin />}
                        />
                    }
                    onClose={toggleDonatePopover}
                    zIndex={1100}
                />
            )}

            {/* Contact Popover */}
            {contactPopoverOpen && (
                <Popover
                    content={
                        <PopoverContentContainer
                            title="CONTACT"
                            component={<Contact />}
                        />
                    }
                    onClose={toggleContactPopover}
                    zIndex={1100}
                />
            )}
        </MainContainer>
    );
}

export default Main;